import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import { loadVacancyStageDetails } from '../../store/directusService';
import { clearBusinessAuditData, loadBusinessAuditData } from '../../store/recruitmentService';
import AuditBusinessModal from '../auditing/auditBusiness/AuditBusinessModal';
import AccordionHeader from '../ui/accordion/AccordionHeader';
import AccordionIcon from '../ui/accordion/AccordionIcon';

import BusinessManagement from './businesses/myBusinesses/BusinessManagement';
import OtherBusinessManagement from './businesses/otherBusinesses/OtherBusinessManagement';
import PoolTableManagement from './vacanciesAndPooling/pools/PoolTableManagement';
import VacancyTableManagement from './vacanciesAndPooling/vacancies/VacancyTableManagement';

import app from '../../app.module.css';

const RecruitmentManagement = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const { state } = useLocation();
    const [expanded, setExpanded] = useState('');
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');

    // STORE STATE
    const vacancyStageDetails = useSelector(
        (state) => state.entities.directusService.vacancyStageDetails
    );

    // USE EFFECTS
    useEffect(() => {
        if (!state?.accordionPanel) setExpanded('');
        else setExpanded(state.accordionPanel);
        vacancyStageDetails?.length < 1 && dispatch(loadVacancyStageDetails());
    }, []);

    // EVENT HANDLERS
   const onPanelChange = (panel) => setExpanded(expanded === panel ? '' : panel);

    const onSearchAuditItems = (id, field, label) => {
        if (id) dispatch(loadBusinessAuditData(id, field));
        setShowAuditModal(true);
        setAuditLabel(label ?? field);
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearBusinessAuditData());
    };

    return (
        <div className={app.pageContainer}>
            <h1 className={app.mainHeading}>Recruitment Management</h1>
            <Accordion
                slotProps={{ transition: { mountOnEnter: true } }}
                expanded={expanded === 'myBusinesses'}
                onChange={() => onPanelChange('myBusinesses')}>
                <AccordionSummary
                    expandIcon={<AccordionIcon />}
                    aria-controls="myBusinesses-content"
                    id="myBusinesses-header">
                    <AccordionHeader>My Businesses</AccordionHeader>
                </AccordionSummary>
                <AccordionDetails>
                    {expanded === 'myBusinesses' && (
                        <BusinessManagement
                            isOpen={expanded === 'myBusinesses'}
                            onSearchAuditItems={onSearchAuditItems}
                        />
                    )}
                </AccordionDetails>
            </Accordion>
            <Accordion
                slotProps={{ transition: { mountOnEnter: true } }}
                expanded={expanded === 'otherBusinesses'}
                onChange={() => onPanelChange('otherBusinesses')}>
                <AccordionSummary
                    expandIcon={<AccordionIcon />}
                    aria-controls="otherBusinesses-content"
                    id="otherBusinesses-header">
                    <AccordionHeader>Other Businesses</AccordionHeader>
                </AccordionSummary>
                <AccordionDetails>
                    {expanded === 'otherBusinesses' && (
                        <OtherBusinessManagement
                            isOpen={expanded === 'otherBusinesses'}
                            onSearchAuditItems={onSearchAuditItems}
                        />
                    )}
                </AccordionDetails>
            </Accordion>
            <Accordion
                slotProps={{ transition: { mountOnEnter: true } }}
                expanded={expanded === 'vacancies'}
                onChange={() => onPanelChange('vacancies')}>
                <AccordionSummary
                    expandIcon={<AccordionIcon />}
                    aria-controls="vacancies-content"
                    id="vacancies-header">
                    <AccordionHeader>Vacancies</AccordionHeader>
                </AccordionSummary>
                <AccordionDetails>
                    {expanded === 'vacancies' && <VacancyTableManagement />}
                </AccordionDetails>
            </Accordion>
            <Accordion
                slotProps={{ transition: { mountOnEnter: true } }}
                expanded={expanded === 'pooling'}
                onChange={() => onPanelChange('pooling')}>
                <AccordionSummary
                    expandIcon={<AccordionIcon />}
                    aria-controls="pooling-content"
                    id="pooling-header">
                    <AccordionHeader>Pooling</AccordionHeader>
                </AccordionSummary>
                <AccordionDetails>
                    {expanded === 'pooling' && <PoolTableManagement />}
                </AccordionDetails>
            </Accordion>
            {showAuditModal && (
                <div id="auditBusinessModal">
                    <AuditBusinessModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        onClose={onAuditModalClose}
                    />
                </div>
            )}
        </div>
    );
};

export default RecruitmentManagement;
